import { NavLink } from 'react-router-dom'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Alert, Button, ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../../layout/page/PageHeader'
import DataTable from '../../../../common/data-table/DataTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheckCircle,
    faCircleNotch,
    faEdit,
    faFileAlt,
    faPaperPlane,
    faQuestionCircle,
    faRedoAlt,
} from '@fortawesome/free-solid-svg-icons'
import { api } from '../../../../../auth'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { useAppSelector } from '../../../../../store/hooks'

const ROUTE_BASE = '/people/clients/email-campaigns'

type TableRow = Record<string, unknown> & { id?: number; subject?: string; status?: string }
type DateFilterOptions = { value: unknown; filterCallback: (v: unknown, i: number) => void; index: number }

const Clients = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const { triggerToast } = useToast()
    const axiosPrivate = api
    const [refreshTable, setRefreshTable] = useState(false)

    const [sendDialogVisible, setSendDialogVisible] = useState(false)
    const [sendTarget, setSendTarget] = useState<TableRow | null>(null)
    const [isSending, setIsSending] = useState(false)

    const dateTimeBodyTemplate = (rowData: TableRow, field: string) => {
        return rowData[field]
            ? dayjs(rowData[field] as string).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options: DateFilterOptions) => {
        return (
            <Calendar
                value={options.value as Date | null | undefined}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const openSendDialog = (rowData: TableRow) => {
        setSendTarget(rowData)
        setSendDialogVisible(true)
    }

    const executeSend = async () => {
        if (!sendTarget) return
        setIsSending(true)

        try {
            const response = await axiosPrivate.post(
                `/newsletters/send/${sendTarget.id}`
            )

            if (response.data?.success) {
                triggerToast({
                    severity: 'success',
                    summary: 'Sent!',
                    detail: response.data.data.msg,
                    life: 5000,
                })
                setRefreshTable(true)
                setSendDialogVisible(false)
            } else {
                const errors = response.data?.errors
                const detail =
                    Array.isArray(errors)
                        ? errors.join(', ')
                        : typeof errors === 'object' && errors
                        ? Object.values(errors).flat().join(', ')
                        : response.data?.error ||
                          response.data?.message ||
                          'Failed to send'
                triggerToast({
                    severity: 'error',
                    summary: 'Error!',
                    detail,
                    life: 5000,
                })
            }
        } catch (err: any) {
            const errors = err?.response?.data?.errors
            const detail =
                Array.isArray(errors)
                    ? errors.join(', ')
                    : typeof errors === 'object' && errors
                    ? Object.values(errors).flat().join(', ')
                    : err?.response?.data?.error ||
                      err?.response?.data?.message ||
                      'Failed to send'
            triggerToast({
                severity: 'error',
                summary: 'Error!',
                detail,
                life: 5000,
            })
        } finally {
            setIsSending(false)
        }
    }

    const sendDialogFooter = (
        <div>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => setSendDialogVisible(false)}
            >
                No
            </Button>
            <Button variant="success" onClick={executeSend}>
                <span className="text-white">
                    {isSending ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin /> Sending...
                        </>
                    ) : (
                        'Yes, Send'
                    )}
                </span>
            </Button>
        </div>
    )

    const actionsBodyTemplate = (rowData: TableRow) => {
        const isResend = rowData.status === 'sent'
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/newsletters/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete{' '}
                            <strong>&quot;{rowData.subject}&quot;</strong>?
                        </p>
                    }
                />
                <NavLink
                    to={`${ROUTE_BASE}/view/${rowData.id}`}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
                <button
                    className={`btn ms-1 ${isResend ? 'btn-outline-warning' : 'btn-outline-success'}`}
                    onClick={() => openSendDialog(rowData)}
                    title={isResend ? 'Re-send' : 'Send'}
                >
                    <FontAwesomeIcon icon={isResend ? faRedoAlt : faPaperPlane} />
                </button>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: TableRow) => {
        switch (rowData.status) {
            case 'sent':
                return (
                    <span className="text-success">
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="me-2"
                        />
                        Sent
                    </span>
                )
            case 'draft':
            default:
                return (
                    <span className="text-secondary">
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        Draft
                    </span>
                )
        }
    }

    return (
        <>
            <PageHeader
                icon="envelope"
                subtitle="Create and send emails to subscribers"
                gradient="linear-gradient(135deg, #f59e0b, #d97706)"
                header="Email Campaigns"
                buttons={[
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'plus',
                        url: `${ROUTE_BASE}/new`,
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                toolbarConfig={{
                    includeExportBtn: true,
                }}
                config={{
                    dataRoute: '/newsletters',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'created_at',
                    sortOrder: -1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        subject: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        template_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        status: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        recipient_filter: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        recipients_count: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        sent_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        created_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'subject',
                        header: 'Subject',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: { width: '26%', minWidth: 0 },
                        style: {
                            width: '26%',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    },
                    {
                        field: 'template_name',
                        header: 'Template',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        body: (rowData: TableRow) =>
                            rowData.template_name || (
                                <span className="text-muted fst-italic">Custom</span>
                            ),
                        headerStyle: { width: '12%', minWidth: 0 },
                        style: { width: '12%', minWidth: 0 },
                    },
                    {
                        field: 'status',
                        header: 'Status',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        body: statusTemplate,
                        fixedCenter: true,
                        alignHeader: 'left',
                        headerStyle: {
                            width: '10%',
                            minWidth: 0,
                            textAlign: 'left',
                        },
                        style: {
                            width: '10%',
                            minWidth: 0,
                            textAlign: 'left',
                        },
                    },
                    {
                        field: 'recipient_filter',
                        header: 'Recipients',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        body: (rowData: TableRow) =>
                            rowData.recipient_filter === 'active'
                                ? 'Active only'
                                : 'All',
                        headerStyle: { width: '12%', minWidth: 0 },
                        style: { width: '12%', minWidth: 0 },
                    },
                    {
                        field: 'recipients_count',
                        header: 'Sent To',
                        sortable: true,
                        body: (rowData: TableRow) =>
                            rowData.recipients_count ?? '—',
                        alignHeader: 'center',
                        headerStyle: { width: '10%', minWidth: 0, textAlign: 'center' },
                        style: { width: '10%', minWidth: 0, textAlign: 'center' },
                    },
                    {
                        field: 'sent_at',
                        header: 'Sent At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData: TableRow) =>
                            dateTimeBodyTemplate(rowData, 'sent_at'),
                        filterElement: dateFilterTemplate,
                        headerStyle: { width: '18%', minWidth: 0 },
                        style: { width: '18%', minWidth: 0 },
                    },
                    {
                        field: 'created_at',
                        header: 'Created At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData: TableRow) =>
                            dateTimeBodyTemplate(rowData, 'created_at'),
                        filterElement: dateFilterTemplate,
                        style: { width: '18%', minWidth: 0 },
                        isVisible: false,
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '12%',
                            minWidth: 0,
                            textAlign: 'center',
                        },
                        style: {
                            width: '12%',
                            minWidth: 0,
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['subject', 'status', 'recipient_filter', 'template_name']}
            />

            <Dialog
                header={
                    <span>
                        <FontAwesomeIcon icon={faQuestionCircle} /> Confirm
                    </span>
                }
                visible={sendDialogVisible}
                breakpoints={{ '960px': '60vw', '640px': '80vw' }}
                style={{ width: '38vw' }}
                onHide={() => setSendDialogVisible(false)}
                footer={sendDialogFooter}
                closable={false}
            >
                {sendTarget?.status === 'sent' ? (
                    <>
                        <p className="mb-3">
                            This email has already been sent. Are you sure you want to
                            re-send <strong>&quot;{sendTarget?.subject}&quot;</strong> to
                            subscribers?
                        </p>
                        <Alert variant="warning" className="mb-0">
                            Recipients who already received this email will get it again.
                        </Alert>
                    </>
                ) : (
                    <p className="mb-0">
                        Are you sure you want to send{' '}
                        <strong>&quot;{sendTarget?.subject}&quot;</strong> to subscribers?
                    </p>
                )}
            </Dialog>
        </>
    )
}

export default Clients
